import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'router-v6'
import { getDefaultRoute } from 'v2/configs/routes'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { type RLoginResponse, type RUserSession, UserRole } from 'v2/types/api/auth'
import { AnalyticsEvents, consumeSignupCountry, identifyUser, track } from 'v2/utils/analytics'
import { logger } from 'v2/utils/logger'
import { getSafeRedirectUrl } from 'v2/utils/url-validation'
import { redirectToTwoFa } from './utils/two-fa-utils'

export const SsoRedirect = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { loginSuccess } = useAuth()

  useEffect(() => {
    const handleSsoRedirect = () => {
      try {
        const authToken = searchParams.get('auth_token')
        const sessionId = searchParams.get('session_id')
        const nextUrl = getSafeRedirectUrl(searchParams.get('next_url'))
        const error = searchParams.get('error')
        const aomUuid = searchParams.get('aom_uuid')
        const rolesParam = searchParams.get('aom_roles')
        const region = searchParams.get('region')
        const name = searchParams.get('name')
        const email = searchParams.get('email')
        const organizationName = searchParams.get('organization_name')
        const organizationUuid = searchParams.get('organization_uuid') || undefined

        // 2FA params
        const twoFaEnabled = searchParams.get('two_fa_enabled') === 'true'
        const redirectTwoFaAuth = searchParams.get('redirect_two_fa_auth') === 'true'
        const phoneNumber = searchParams.get('phone_number') || ''
        const sessionUuid = searchParams.get('session_uuid') || ''
        const accountId = searchParams.get('account_id') || ''
        const authenticatorIsDefault = searchParams.get('authenticator_is_default') === 'true'
        const authenticatorIsPresent = searchParams.get('authenticator_is_present') === 'true'

        if (error) {
          logger.error('SSO login error:', error)
          navigate(`/login?sso_error=${encodeURIComponent(error)}`)
          return
        }

        // Google signup: hodor echoes flow=signup and the Fingerprint ids from /authorize.
        // Fires the completion event only with the identifiers hodor sets on a completed signup.
        const recordGoogleSignup = () => {
          if (
            searchParams.get('flow') !== 'signup' ||
            !email ||
            !sessionId ||
            !aomUuid ||
            !accountId
          ) {
            return
          }
          const fingerprintVisitorId = searchParams.get('fingerprint_visitor_id') || undefined
          identifyUser({
            email,
            name: name || undefined,
            orgName: organizationName || undefined,
            region: region || undefined,
            fingerprintVisitorId,
          })
          track(AnalyticsEvents.USER_SIGNED_UP, {
            org_name: organizationName,
            region,
            email,
            country: consumeSignupCountry(),
            sso: 'google',
            fingerprint_visitor_id: fingerprintVisitorId,
            fingerprint_event_id: searchParams.get('fingerprint_event_id') || undefined,
          })
        }

        // Parse and validate comma-separated roles
        const validRoles = Object.values(UserRole)
        const roles = rolesParam
          ? rolesParam.split(',').filter((r): r is UserRole => validRoles.includes(r as UserRole))
          : []

        if (twoFaEnabled) {
          const originalLoginResponse: RLoginResponse = {
            auth_token: authToken || '',
            browser_session_id: sessionId || '',
            aom_uuid: aomUuid || '',
            organization_name: organizationName || '',
            organization_uuid: organizationUuid,
            region: region || '',
            roles,
            email: email || '',
            name: name || '',
            two_fa_enabled: true,
            redirect_two_fa_auth: redirectTwoFaAuth,
            phone_number: phoneNumber,
            session_uuid: sessionUuid,
            account_id: Number(accountId),
            authenticator_status: {
              is_default: authenticatorIsDefault,
              is_present: authenticatorIsPresent,
            },
          }

          recordGoogleSignup()
          redirectToTwoFa({
            loginResponse: originalLoginResponse,
            navigate,
            source: 'sso',
            nextUrl: nextUrl || '',
          })
          return
        }

        // No 2FA — direct login
        if (!authToken) {
          logger.error('No auth token found in URL')
          navigate('/login')
          return
        }

        const authData: RUserSession = {
          auth_token: authToken,
          browser_session_id: sessionId || '',
          aom_uuid: aomUuid || '',
          organization_name: organizationName || '',
          organization_uuid: organizationUuid,
          region: region || '',
          roles,
          email: email || '',
          name: name || '',
        }

        recordGoogleSignup()
        loginSuccess(authData)
        navigate(nextUrl || getDefaultRoute())
      } catch (error) {
        logger.error('Error handling SSO redirect:', error)
        navigate('/login')
      }
    }

    handleSsoRedirect()
  }, [loginSuccess, navigate, searchParams])

  return null
}
